import { useCallback, useEffect, useState } from "react";
import { Flex, IconButton } from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import { TranslatedText, useT } from "../../utilities/TranslatedText";
import styles from "./styles/ScrollHint.module.scss";

export const scrollRowClassName = styles.row;

export function useScrollButtons() {
  const [row, setRow] = useState<HTMLElement | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const update = useCallback(() => {
    if (!row) return;
    setCanScrollPrev(row.scrollLeft > 0);
    setCanScrollNext(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
  }, [row]);

  // adding or removing items changes scrollWidth without resizing the row
  useEffect(update);

  useEffect(() => {
    if (!row) return;
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [row, update]);

  function scrollByItem(direction: 1 | -1) {
    const item = row?.firstElementChild as HTMLElement | null;
    if (!row || !item) return;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    row.scrollBy({
      left: direction * (item.offsetWidth + gap),
      behavior: "smooth",
    });
  }

  return {
    rowRef: setRow,
    onScroll: update,
    canScrollPrev,
    canScrollNext,
    overflows: canScrollPrev || canScrollNext,
    scrollByItem,
  };
}

export function ScrollHint(props: {
  children: string;
  scroll: ReturnType<typeof useScrollButtons>;
}) {
  const t = useT();
  const { canScrollPrev, canScrollNext, scrollByItem } = props.scroll;

  return (
    <Flex align={"center"} gap={"3"} className={styles.scrollHint}>
      <TranslatedText size={"2"} weight={"medium"}>
        {props.children}
      </TranslatedText>
      <Flex gap={"2"} className={styles.scrollButtons}>
        <IconButton
          size={"3"}
          radius={"full"}
          variant={"surface"}
          aria-label={t("Previous")}
          disabled={!canScrollPrev}
          onClick={() => scrollByItem(-1)}
        >
          <FontAwesome prefix={"fas"} name={"fa-chevron-left"} />
        </IconButton>
        <IconButton
          size={"3"}
          radius={"full"}
          variant={"surface"}
          aria-label={t("Next")}
          disabled={!canScrollNext}
          onClick={() => scrollByItem(1)}
        >
          <FontAwesome prefix={"fas"} name={"fa-chevron-right"} />
        </IconButton>
      </Flex>
    </Flex>
  );
}
